import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { getTcsmEpiProps, setCicSettings, setGpsSetting, setLabelSetting, setMapPortalSettings } from '../global/ducks/epi-api';
import { setCurrentLanguage } from '../global/ducks/globalization';
import { TcsmEpiApiState, TcsmStates } from './tcsm-model';
import { getBasePath } from '../global/helpers/routing';
import { ArcRoutes } from '../pages/arc/constants/routes';
import { AccountInfoRoutes } from '../pages/account-info/constants/routes';
import { ArcApp } from '../pages/arc/components/arc-app';
import { useDeserializedToken } from '../pages/arc/components/use-deserialized-token';
import { useAdminStatus } from '../global/hooks/useAdminStatus';
import { ForbiddenErrorMessage } from '../global/components/forbidden-error-message';
import { DemographicAppKeys, DemographicsContext } from '../global/models/demographics-model';
import { PersonManagementApp } from '../pages/account-info/components/person-management-app';
import { getMlpsLov, getOrgLov, getPersonLov, setExternalLinks, setMlpsLov, setOrgLov, setPersonLov } from '../global/ducks/lov-proc-api';
import { getExternalLinks } from '../global/ducks/mlps-api';
import { getNotifications } from '../global/ducks/notification-prc-api';
import { setNotifications } from '../pages/notification/notification-slice';
import { NotificationStatus } from '../pages/notification/notification-models';
import { apiErrorClear } from '../global/ducks/base-api';
import { ExternalLinkModal } from '../global/components/external-link-modal';
import { PageFactory } from '../global/components/page-factory';
import { MyCpgLandingPage } from '../pages/mycpg-pods/mycpg-landing-page';
import { useInitialState } from './hooks/useInitialState';
import { TcsmStateManager } from '../global/components/tcsm-state-manager';
import { useDemographicsPerson } from '../pages/account-info/hooks/useDemographicsPerson';
import { useMyCpgMaintenance } from '../global/hooks/useMaintenance';
import { MaintenancePage } from '../global/components/maintenance-page';
import { ClergyCompensationReportApp } from '../pages/clergy-compensation-report/clergy-compensation-report-app';
import { TcsmAppConfigs } from '../global/models/configuration-models';
import { useRoleObject } from '../pages/arc/components/useRoles';
import { RouteHistoryTracking } from '../global/helpers/analytics-helper';
import { ErrorBoundaryApp } from '../global/components/error-boundary';
import { CicOptInForm } from '../pages/arc/features/cic-opt/cic-opt-in-form';
import { LabelSettingProps } from '../global/models/epi-model';
import { initKaiserZipCodes } from '../global/helpers/kaiser';

interface Props {
	appConfig: TcsmAppConfigs
}
export function TcsmApp(props: Props) {
	const { appConfig } = props;
	const dispatch = useDispatch();
	const token = useDeserializedToken();
	const roles = useRoleObject();
	const isAdmin = useAdminStatus(roles);
	const guid = useSelector<TcsmStates, string>(state => state.authenticatedState?.clientGuid);

	useInitialState();
	// gets needed content/data that is shared across features
	const personLoaded = useDemographicsPerson(DemographicAppKeys.myCPG);

	useEffect(() => {
		if (personLoaded) {
			dispatch(getTcsmEpiProps(dispatch, appConfig.contentId, appConfig.currentCulture));
			dispatch(getTcsmEpiProps(dispatch, appConfig.labelSettingId, { guid }, appConfig.currentCulture, false, setLabelSetting));
			dispatch(getTcsmEpiProps(dispatch, appConfig.guidedProcessSupportSettingId, { guid }, appConfig.currentCulture, false, setGpsSetting));
			dispatch(getTcsmEpiProps(dispatch, appConfig.siteSettingsId, { guid }, appConfig.currentCulture, true));
			dispatch(getTcsmEpiProps(dispatch, appConfig?.mapPortalSettingsId, { guid }, appConfig.currentCulture, false, setMapPortalSettings));
			dispatch(getTcsmEpiProps(dispatch, appConfig?.cicSettingsId, { guid }, appConfig.currentCulture, false, setCicSettings));
			dispatch(setCurrentLanguage(appConfig.currentCulture));
			dispatch(getPersonLov((response) => dispatch(setPersonLov(response))));
			dispatch(getOrgLov((response) => dispatch(setOrgLov(response))));
			dispatch(getMlpsLov((response) => dispatch(setMlpsLov(response))));
			dispatch(getExternalLinks((response) => dispatch(setExternalLinks(response))));
			dispatch(getNotifications(token?.person?.guid,
				(response) => dispatch(setNotifications(response?.data?.filter(a => a.notification_status === NotificationStatus.INITIAL || a.notification_status === NotificationStatus.DISMISSED))),
				() => dispatch(apiErrorClear()),
			));
		}
	}, [personLoaded]);

	const location = useLocation()?.pathname;

	useEffect(() => {
		if (location) {
			window.scrollTo(0, 0);
		}
	}, [location]);

	const labelSetting = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps.labelSettingState.epiContent);
	useEffect(() => {
		initKaiserZipCodes(labelSetting?.zipCodes);
	}, [labelSetting?.zipCodes]);

	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const myCpgMaintenance = useMyCpgMaintenance();

	if (!epiProps.contentState.contentLoaded
		|| !epiProps.siteSettingsState.siteSettingsLoaded
		|| !epiProps.labelSettingState.contentLoaded) {
		return <LoadingCircle />;
	}

	const basePath = getBasePath(appConfig.currentCulture);


	return (
		<main id="main">
			<ErrorBoundaryApp>
				<RouteHistoryTracking />
				<TcsmStateManager />
				<ExternalLinkModal navigation={{
					...appConfig?.navigation,
					...appConfig?.cicSettings,
				}} />
				<Switch>
					<Route exact path={`${basePath}`}
						render={() => <Redirect to={isAdmin ? `${basePath}/${ArcRoutes.ArcRoot}` : `${basePath}/${AccountInfoRoutes.MyCpgRoot}`} />}
					/>
					<Route exact path={`${basePath}/clergy-compensation-report`}>
						<ClergyCompensationReportApp settingsId={appConfig.cleryCompReportId} language={appConfig.currentCulture} />
					</Route>
					<Route
						exact
						path={`${basePath}/${ArcRoutes.CicOptInForm}`}
						render={() =>
							<CicOptInForm />}
					/>
					<Route exact path={`${basePath}/${AccountInfoRoutes.MyCpgRoot}`}
						render={() => (
							myCpgMaintenance ?
								<MaintenancePage message={epiProps?.siteSettingsState?.siteSettings?.myCpgSiteMaintenanceText} /> :
								<DemographicsContext.Provider value={DemographicAppKeys.myCPG}>
									<MyCpgLandingPage podsSettingsId={appConfig?.podsSettingsId} appConfig={appConfig} />
								</DemographicsContext.Provider>
						)}
					/>
					<Route
						path={`${basePath}/${AccountInfoRoutes.MyCpgRoot}`}
						render={() => (
							myCpgMaintenance ?
								<MaintenancePage message={epiProps?.siteSettingsState?.siteSettings?.myCpgSiteMaintenanceText} /> :
								<>
									<DemographicsContext.Provider value={DemographicAppKeys.myCPG}>
										<PersonManagementApp
											appConfig={appConfig}
											isArc={false}
										/>
									</DemographicsContext.Provider>

								</>
						)}
					/>
					<Route
						path={`${basePath}/${ArcRoutes.ArcRoot}`}
						render={() => (
							isAdmin ? <ArcApp
								appConfig={appConfig}
							/> : <ForbiddenErrorMessage code={403} />
						)}
					/>

					<Route path={`${basePath}/*`} render={() => <PageFactory />} />
				</Switch>
			</ErrorBoundaryApp>
		</main>
	);
}